"use client";
import IntroductionVideoListSelectModal from "@/app/(introduction-video)/_components/introduction-video/IntroductionVideoListSelectModal";
import PostcodeSelector from "@/app/(job-posting)/_components/PostcodeSelector";
import WorkExperienceBox from "@/app/(resume)/_components/WorkExperienceBox";
import { subscribePushNotification } from "@/app/(sign-up)/_services/auth.action";
import PhoneInputFormWithKoreanFormat from "@/app/_components/share/ApplyJob/PhoneInputFormWithKoreanFormat";
import AsyncSelect from "@/app/_components/share/AsyncSelect";
import DateInputField from "@/app/_components/share/DateInputField";
import EmailInputFormWithDomainSelecter from "@/app/_components/share/EmailInputFormWithDomainSelecter";
import { useModal } from "@/app/_components/share/Modal";
import MultipleFileUploadInput from "@/app/_components/share/MultipleFileUploadInput";
import YoutubeVideoPlayerIframe from "@/app/_components/share/YoutubeVideoPlayerIframe";
import { toast } from "@/app/providers";
import {
  getBrands,
  getCareers,
  getCities,
  getDistricts,
  getJobDuty,
} from "@/services/api/masterData";
import {
  getResumes,
  postCreateResume,
  postPersonalInfo,
  postUpdateResume,
} from "@/services/api/personal";
import { ImageAcceptType, ResumeStatuKeys } from "@/services/constants/global";
import Yup, {
  MAX_LENGTH_INPUT,
  MAX_LENGTH_TEXTAREA,
  attachments,
  email,
  fullName,
  postcodeAddress,
  requiredDateSchema,
  requiredStringSchema,
  workExperiences,
} from "@/services/constants/validateSchema";
import useForm from "@/services/hooks/useForm";
import useMultiLanguageConstant from "@/services/hooks/useMultiLanguageConstant";
import { useSetUserInfo, useUserInfo } from "@/services/hooks/useUserInfo";
import { formatDate, objectToFormData } from "@/services/utils/formatter";
import { objectRemoveKeys } from "@/services/utils/general";
import {
  Avatar,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { get } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCirclePlus, FaTrash } from "react-icons/fa6";
import { useMutation, useQuery } from "react-query";

const defaultWorkExperience = {
  companyName: "",
  position: "",
  startPeriod: "",
  endPeriod: "",
};

export default function ResumeRegisterForm({
  hideHeader = false,
  onSubmitSuccess = () => {},
}) {
  const { t } = useTranslation();
  const { Gender, Education, ResumeStatus } = useMultiLanguageConstant();
  const [openModal, closeModal] = useModal();
  const userInfo = useUserInfo();
  const setUserInfo = useSetUserInfo();

  const renderGender = Gender.filter((item) => item.value !== "IRRELEVANT");

  const [updatePersonalInfo, setUpdatePersonalInfo] = useState(false);

  const { data: resumeData } = useQuery("resumeDetail", async () =>
    getResumes()
  );

  const mutation = useMutation((resume) => {
    if (resumeData) return postUpdateResume(resume);
    return postCreateResume(resume);
  });
  const updatePersonalInfoMutation = useMutation((personal) => {
    return postPersonalInfo(personal);
  });

  const initialValues = resumeData
    ? {
        attachments: [],
        birthday: get(userInfo, "birthday"),
        detailAddress: get(userInfo, "detailAddress"),
        postcodeAddress: get(userInfo, "postcodeAddress"),
        getNotification: true,
        ...resumeData,
        gender:
          get(resumeData, "gender") === "IRRELEVANT"
            ? Gender[0].value
            : get(resumeData, "gender", Gender[0].value),
        id: get(resumeData, "_id"),
        workLocation: get(resumeData, "workLocation", [
          {
            city: "",
            district: "",
          },
        ]).map((item) => ({
          city: get(item, "city._id"),
          district: get(item, "district._id"),
        })),
        dutyCareer: get(resumeData, "dutyCareer", [
          {
            duty: "",
            career: "",
          },
        ]).map((item) => ({
          duty: get(item, "duty._id"),
          career: get(item, "career._id"),
        })),
        position: get(resumeData, "position._id", "663b2ccf9679593ff51b5d18"),
        brand: get(resumeData, "brand", [""]).map((item) => item._id),
        attachmentDeletes: [],
        userIntroductionVideos: get(resumeData, "userIntroductionVideos") || [],
        workExperiences: get(resumeData, "workExperiences").map((item) => {
          return {
            ...item,
            position: item.position?._id || "",
          };
        }),
      }
    : {
        dutyCareer: [
          {
            duty: "",
            career: "",
          },
        ],
        title: "",
        position: "663b2ccf9679593ff51b5d18",
        brand: [""],
        workLocation: [
          {
            city: "",
            district: "",
          },
        ],
        fullName: get(userInfo, "fullName"),
        gender: get(userInfo, "gender", Gender[0].value),
        birthday: get(userInfo, "birthday"),
        detailAddress: get(userInfo, "detailAddress"),
        postcodeAddress: get(userInfo, "postcodeAddress"),
        email: get(userInfo, "email"),
        phone: get(userInfo, "phone"),
        workExperiences: [],
        attachments: [],
        getNotification: true,
        userIntroductionVideos: [],
      };

  const validateSchema = Yup.object().shape({
    // title: requiredStringTextOrNumberOnlySchema,
    fullName,
    attachments,
    email: email,
    // phone: phone,
    postcodeAddress,
    dutyCareer: Yup.array().of(
      Yup.object().shape({
        duty: requiredStringSchema,
        career: requiredStringSchema,
      })
    ),
    workExperiences,
    workLocation: Yup.array().of(
      Yup.object().shape({
        city: requiredStringSchema,
        district: requiredStringSchema,
      })
    ),
    brand: Yup.array().of(requiredStringSchema),
    birthday: requiredDateSchema,
  });

  const onSubmitForm = (values) => {
    if (formik.dirty) {
      const tempValues = {
        ...values,
        isReceiveInterview: values.status === ResumeStatuKeys.OPEN,
        attachments: values.attachments?.filter((item) => item instanceof File),
        userIntroductionVideos: values.userIntroductionVideos?.map(
          (item) => item._id
        ),
      };

      const removeKeys = ["user", "_id"];

      if (!(tempValues.avatar instanceof File)) removeKeys.push("avatar");

      const formattedData = objectToFormData(
        objectRemoveKeys(tempValues, removeKeys)
      );

      if (get(formattedData, "getNotification")) subscribePushNotification();

      mutation.mutate(formattedData, {
        onSuccess: () => {
          setUpdatePersonalInfo(true);
        },
      });
    } else {
      onSubmitSuccess();
    }
  };

  const formik = useForm({
    initialValues: initialValues,
    validationSchema: validateSchema,
    onSubmit: onSubmitForm,
    enableReinitialize: true,
  });

  const handleWorkExperienceBoxContentChange = (index, value) => {
    const currentFileValue = { ...formik.values?.workExperiences?.[index] };
    const tempWorkExperiences = formik.values?.workExperiences
      ? [...formik.values.workExperiences]
      : [];
    tempWorkExperiences[index] = { ...currentFileValue, ...value };
    formik.setFieldValue("workExperiences", [...tempWorkExperiences]);
  };

  const onAddWorkExperienceClick = () => {
    const currentFileValue = formik.values?.workExperiences || [];
    formik.setFieldValue("workExperiences", [
      ...currentFileValue,
      defaultWorkExperience,
    ]);
  };

  const onRemoveWorkExperienceClick = (index) => {
    const newArr = formik.values.workExperiences.filter(
      (_, itemIndex) => itemIndex !== index
    );

    formik.setFieldValue("workExperiences", newArr);
  };

  const onRemoveFileClick = (index) => {
    const removeAttachmentKey = formik.values.attachments[index].key;
    if (removeAttachmentKey)
      formik.setFieldValue("attachmentDeletes", [
        ...formik.values.attachmentDeletes,
        removeAttachmentKey,
      ]);
    const newFiles = formik.values.attachments.filter(
      (_, itemIndex) => itemIndex !== index
    );
    formik.setFieldValue("attachments", newFiles);
  };

  const onSelectIntroductionVideoClick = () => {
    const onSelect = (video) => {
      formik.setFieldValue("userIntroductionVideos", video);
      closeModal();
    };
    openModal({
      title: t("Select Introduction Video"),
      body: (
        <IntroductionVideoListSelectModal
          selectedIds={formik.values.userIntroductionVideos}
          onSelect={onSelect}
        />
      ),
      isCompact: true,
    });
  };

  const avatarImageUrl = useMemo(() => {
    if (!formik.values.avatar) return "";
    if (formik.values.avatar.url) return formik.values.avatar.url;
    if (typeof formik.values.avatar === "object")
      return URL.createObjectURL(formik.values.avatar);
    return "";
  }, [formik.values.avatar]);

  const onAddBrandClick = () => {
    const currentFileValue = formik.values?.brand || [];
    formik.setFieldValue("brand", [...currentFileValue, ""]);
  };

  const onRemoveBrandClick = (id) => {
    const newArr = formik.values.brand.filter((item) => item !== id);
    formik.setFieldValue("brand", newArr);
  };

  const onAddWorkLocationClick = () => {
    const currentFileValue = formik.values?.workLocation || [];
    formik.setFieldValue("workLocation", [
      ...currentFileValue,
      { city: "", district: "" },
    ]);
  };

  const onRemoveWorkLocation = (index) => {
    const newArr = formik.values.workLocation.filter(
      (_, itemIndex) => itemIndex !== index
    );
    formik.setFieldValue("workLocation", newArr);
  };

  const handleUpdateArrValue = (arrName, index, value) => {
    const currentFileValue = formik.values?.[arrName] || [];
    const tempArr = [...currentFileValue];
    tempArr[index] = value;
    formik.setFieldValue(arrName, [...tempArr]);
  };

  const onAddPositionClick = () => {
    const currentFileValue = formik.values?.dutyCareer || [];
    formik.setFieldValue("dutyCareer", [
      ...currentFileValue,
      { duty: "", career: "" },
    ]);
  };

  const onRemoveArrItem = (arrName, index) => {
    const newArr = formik.values[arrName].filter(
      (_, itemIndex) => itemIndex !== index
    );
    formik.setFieldValue(arrName, newArr);
  };

  useEffect(() => {
    if (updatePersonalInfo && userInfo) {
      const payload = {
        ...userInfo,
        fullName: formik.values.fullName,
        gender: formik.values.gender,
        birthday: formik.values.birthday,
        postcodeAddress: formik.values.postcodeAddress,
        detailAddress: formik.values.detailAddress,
        phone: formik.values.phone,
        email: formik.values.email,
        avatar: formik.values.avatar,
        isCheckPhoneDuplicate: false,
      };
      const formattedData =
        payload.avatar instanceof File
          ? { ...payload }
          : objectRemoveKeys(payload, ["avatar"]);
      const formData = objectToFormData(formattedData);
      updatePersonalInfoMutation.mutate(formData, {
        onSuccess: (res) => {
          setUserInfo({ ...res });
          toast({
            title: resumeData
              ? t("Update successfully")
              : t("Create successfully"),
            status: "success",
          });
          onSubmitSuccess();
        },
      });
      setUpdatePersonalInfo(false);
    }
  }, [updatePersonalInfo, formik, userInfo]);

  return (
    <Stack as="form" id="resume-form" onSubmit={formik.handleSubmit} noValidate>
      {!hideHeader && (
        <HStack
          bg="white"
          position="sticky"
          top={98}
          zIndex={10}
          bgColor="white"
          rounded="md"
          justifyContent="space-between"
          p={4}
          boxShadow="0px 4px 16px 0px rgba(210, 208, 208, 0.25);"
        >
          <Text fontWeight="semibold">
            {resumeData ? t("Edit resume") : t("Add new resume")}
          </Text>
          <Button
            isLoading={
              mutation.isLoading || updatePersonalInfoMutation.isLoading
            }
            loadingText={t("Saving...")}
            type="submit"
            isDisabled={!!onSubmitSuccess && !formik.dirty}
          >
            {t("Save")}
          </Button>
        </HStack>
      )}
      <Flex
        justifyContent="center"
        bgColor="white"
        rounded="md"
        py={hideHeader ? 2 : 10}
      >
        <Container maxW="container.md">
          <Stack gap={10}>
            <Stack gap={4}>
              {/* <FormControl>
                <FormLabel>{t("Job Objective")}</FormLabel>
                <Input
                  name="jobObjective"
                  value={formik.values.jobObjective}
                  onChange={formik.handleChange}
                  maxLength={MAX_LENGTH_INPUT}
                />
              </FormControl> */}
              <FormControl isRequired>
                <FormLabel>{t("Preferred work location")}</FormLabel>
                <Stack>
                  {get(formik, "values.workLocation").map((item, index) => (
                    <HStack
                      key={index}
                      align="start"
                      spacing={6}
                      justify="space-between"
                    >
                      <FormControl
                        isRequired
                        isInvalid={
                          formik.touched.workLocation &&
                          formik.errors.workLocation?.[index]?.city
                        }
                      >
                        <AsyncSelect
                          queryKey="city"
                          apiCall={getCities}
                          name="city"
                          placeholder={t("Select city")}
                          value={get(item, "city")}
                          onChange={(e) =>
                            handleUpdateArrValue("workLocation", index, {
                              ...item,
                              city: e.target.value,
                            })
                          }
                        />
                        <FormErrorMessage>
                          {formik.errors.workLocation?.[index]?.city}
                        </FormErrorMessage>
                      </FormControl>
                      {get(item, "city") ? (
                        <FormControl
                          isRequired
                          isInvalid={
                            formik.touched.workLocation &&
                            formik.errors.workLocation?.[index]?.district
                          }
                        >
                          <AsyncSelect
                            queryKey={["district", get(item, "city")]}
                            apiCall={async () =>
                              getDistricts(get(item, "city"))
                            }
                            name="district"
                            placeholder={t("--Select--")}
                            isDisabled={!get(item, "city")}
                            value={get(item, "district")}
                            onChange={(e) =>
                              handleUpdateArrValue("workLocation", index, {
                                ...item,
                                district: e.target.value,
                              })
                            }
                          />
                          <FormErrorMessage>
                            {formik.errors.workLocation?.[index]?.district}
                          </FormErrorMessage>
                        </FormControl>
                      ) : (
                        <Select
                          placeholder={t("--Select--")}
                          isDisabled
                        ></Select>
                      )}
                      <IconButton
                        pt={2}
                        h={6}
                        w="fit-content"
                        p={0}
                        variant="ghost"
                        icon={<FaTrash />}
                        onClick={() => onRemoveWorkLocation(index)}
                        isDisabled={
                          get(formik, "values.workLocation", []).length === 1
                        }
                      />
                    </HStack>
                  ))}
                  {get(formik, "values.workLocation", []).length < 3 && (
                    <Button
                      w="fit-content"
                      color="secondary"
                      variant="ghost"
                      leftIcon={<FaCirclePlus />}
                      onClick={onAddWorkLocationClick}
                    >
                      {t("Add more")}
                    </Button>
                  )}
                </Stack>
              </FormControl>
              <FormControl>
                <FormLabel>{t("Preferred brand")}</FormLabel>
                <Stack>
                  {get(formik, "values.brand").map((item, index) => (
                    <HStack align="start" key={index} justify="space-between">
                      <FormControl
                        isRequired
                        isInvalid={
                          formik.touched.brand && formik.errors.brand?.[index]
                        }
                      >
                        <AsyncSelect
                          queryKey="brand"
                          apiCall={getBrands}
                          name="brand"
                          placeholder={t("Select brand")}
                          value={item}
                          onChange={(e) =>
                            handleUpdateArrValue("brand", index, e.target.value)
                          }
                        />
                        <FormErrorMessage>
                          {formik.errors.brand?.[index]}
                        </FormErrorMessage>
                      </FormControl>
                      <IconButton
                        pt={2}
                        h={6}
                        w="fit-content"
                        p={0}
                        variant="ghost"
                        icon={<FaTrash />}
                        onClick={() => onRemoveBrandClick(item)}
                      />
                    </HStack>
                  ))}
                  {get(formik, "values.brand", []).length < 3 && (
                    <Button
                      w="fit-content"
                      color="secondary"
                      variant="ghost"
                      leftIcon={<FaCirclePlus />}
                      onClick={onAddBrandClick}
                    >
                      {t("Add more")}
                    </Button>
                  )}
                </Stack>
              </FormControl>
              <FormControl isRequired>
                <FormLabel as="strong">{t("Preferred duty")}</FormLabel>
                <Stack>
                  {get(formik, "values.dutyCareer").map((item, index) => (
                    <HStack
                      align="start"
                      key={index}
                      spacing={6}
                      justify="space-between"
                    >
                      <FormControl
                        isInvalid={
                          formik.touched.dutyCareer &&
                          formik.errors.dutyCareer?.[index]?.duty
                        }
                      >
                        <AsyncSelect
                          queryKey="duty"
                          apiCall={getJobDuty}
                          name="duty"
                          placeholder={t("Select duty")}
                          value={get(item, "duty")}
                          onChange={(e) =>
                            handleUpdateArrValue("dutyCareer", index, {
                              ...item,
                              duty: e.target.value,
                            })
                          }
                        />
                        <FormErrorMessage>
                          {formik.errors.dutyCareer?.[index]?.duty}
                        </FormErrorMessage>
                      </FormControl>
                      <FormControl
                        isInvalid={
                          formik.touched.dutyCareer &&
                          formik.errors.dutyCareer?.[index]?.career
                        }
                      >
                        <AsyncSelect
                          queryKey="career"
                          apiCall={getCareers}
                          name="career"
                          placeholder={t("Select career")}
                          value={get(item, "career")}
                          onChange={(e) =>
                            handleUpdateArrValue("dutyCareer", index, {
                              ...item,
                              career: e.target.value,
                            })
                          }
                          isSort={false}
                        />
                        <FormErrorMessage>
                          {formik.errors.dutyCareer?.[index]?.career}
                        </FormErrorMessage>
                      </FormControl>
                      <IconButton
                        pt={2}
                        h={6}
                        w="fit-content"
                        p={0}
                        variant="ghost"
                        icon={<FaTrash />}
                        onClick={() => {
                          onRemoveArrItem("dutyCareer", index);
                        }}
                        isDisabled={
                          get(formik, "values.dutyCareer", []).length === 1
                        }
                      />
                    </HStack>
                  ))}
                  {get(formik, "values.dutyCareer", []).length < 3 && (
                    <Button
                      w="fit-content"
                      color="secondary"
                      variant="ghost"
                      leftIcon={<FaCirclePlus />}
                      onClick={onAddPositionClick}
                    >
                      {t("Add more")}
                    </Button>
                  )}
                </Stack>
              </FormControl>
            </Stack>
            <Stack gap={4}>
              <FormLabel htmlFor="getNotification">
                {t("Recruitment Notification")}
              </FormLabel>
              <FormControl display="flex" alignItems="center">
                <HStack>
                  <Switch
                    id="getNotification"
                    name="getNotification"
                    isChecked={formik.values.getNotification}
                    onChange={(e) =>
                      formik.setFieldValue(e.target.name, e.target.checked)
                    }
                  />
                  <FormLabel
                    htmlFor="getNotification"
                    mb={0}
                    fontWeight="normal"
                  >
                    {formik.values.getNotification ? t("Agree") : t("Disagree")}
                  </FormLabel>
                </HStack>
              </FormControl>
            </Stack>
            <Stack gap={4}>
              <FormLabel htmlFor="interview">
                {t("Resume Access by companies")}
              </FormLabel>
              <FormControl display="flex" alignItems="center">
                <HStack>
                  <Switch
                    id="status"
                    name="status"
                    isChecked={formik.values.status === "OPEN"}
                    onChange={(e) =>
                      formik.setFieldValue(
                        e.target.name,
                        e.target.checked ? "OPEN" : "NOT_OPEN"
                      )
                    }
                  />
                  <FormLabel htmlFor="status" mb="0" fontWeight="normal">
                    {ResumeStatus[formik.values.status]}
                  </FormLabel>
                </HStack>
              </FormControl>
            </Stack>

            <Stack gap={4}>
              <Stack
                justifyContent="center"
                alignItems="center"
                py={6}
                gap={4}
                bgColor="#F3F8FF"
                rounded="md"
              >
                <Avatar
                  src={avatarImageUrl || "/icons/upload_icon.svg"}
                  h={140}
                  w={140}
                  position="relative"
                >
                  <Input
                    name="avatar"
                    type="file"
                    cursor="pointer"
                    position="absolute"
                    h="full"
                    w="full"
                    opacity={0}
                    accept={ImageAcceptType}
                    onChange={(e) =>
                      formik.setFieldValue("avatar", e.target.files[0])
                    }
                  />
                </Avatar>
                <Text variant="secondary" textTransform="capitalize">
                  {t("Add photo")}
                </Text>
              </Stack>
              <SimpleGrid columns={2} gap={3}>
                <FormControl
                  isRequired
                  isInvalid={formik.touched.fullName && formik.errors.fullName}
                >
                  <FormLabel>{t("Name")}</FormLabel>
                  <Input
                    name="fullName"
                    onChange={formik.handleChange}
                    value={formik.values.fullName}
                    maxLength={MAX_LENGTH_INPUT}
                  />
                  <FormErrorMessage>{formik.errors.fullName}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isRequired
                  isInvalid={formik.touched.birthday && formik.errors.birthday}
                >
                  <FormLabel>{t("Date of birth")}</FormLabel>
                  <DateInputField
                    name="birthday"
                    value={formatDate(formik.values.birthday)}
                    setFieldValue={formik.setFieldValue}
                    max={formatDate(new Date())}
                    showYearDropdown
                  />
                  <FormErrorMessage>{formik.errors.birthday}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>
              <Stack>
                <EmailInputFormWithDomainSelecter
                  value={formik.values.email}
                  setValue={formik.setFieldValue}
                  error={formik.errors.email}
                  touched={formik.touched.email}
                />
                <PhoneInputFormWithKoreanFormat
                  value={formik.values.phone}
                  setValue={formik.setFieldValue}
                  error={formik.errors.phone}
                  touched={formik.touched.phone}
                />
              </Stack>
              <FormControl isRequired>
                <FormLabel htmlFor="gender">{t("Gender")}</FormLabel>
                <RadioGroup
                  name="gender"
                  onChange={(value) => formik.setFieldValue("gender", value)}
                  value={formik.values.gender}
                >
                  <Stack gap={3}>
                    {renderGender.map((item) => (
                      <Radio key={item.value} value={item.value}>
                        <Text fontSize="sm">{item.label}</Text>
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              </FormControl>
              <FormControl
                isRequired
                isInvalid={
                  formik.touched.postcodeAddress &&
                  formik.errors.postcodeAddress
                }
              >
                <FormLabel>{t("Address")}</FormLabel>
                <PostcodeSelector
                  value={formik.values?.postcodeAddress}
                  onChange={(value) =>
                    formik.setFieldValue("postcodeAddress", value)
                  }
                />
                <FormErrorMessage>
                  {formik.errors.postcodeAddress}
                </FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={
                  formik.touched.detailAddress && formik.errors.detailAddress
                }
              >
                <FormLabel>{t("Detail Address")}</FormLabel>
                <Input
                  name="detailAddress"
                  placeholder={t("Enter address")}
                  value={formik.values.detailAddress}
                  onChange={formik.handleChange}
                  maxLength={MAX_LENGTH_INPUT}
                />
                <FormErrorMessage>
                  {formik.errors.detailAddress}
                </FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>{t("Education")}</FormLabel>
                <Stack gap={4}>
                  <Input
                    name="education"
                    placeholder={t("Enter education")}
                    value={formik.values.education}
                    onChange={formik.handleChange}
                  />
                  <RadioGroup
                    name="educationStatus"
                    onChange={(value) =>
                      formik.setFieldValue("educationStatus", value)
                    }
                    value={formik.values.educationStatus}
                  >
                    <Stack gap={3}>
                      {Education.map((item) => (
                        <Radio key={item.value} value={item.value}>
                          <Text fontSize="sm">{item.label}</Text>
                        </Radio>
                      ))}
                    </Stack>
                  </RadioGroup>
                </Stack>
              </FormControl>
              <FormControl>
                <FormLabel>{t("Skills")}</FormLabel>
                <Textarea
                  name="skills"
                  placeholder={t("Write somethings ...")}
                  value={formik.values.skills}
                  onChange={formik.handleChange}
                  resize="none"
                  maxLength={MAX_LENGTH_TEXTAREA}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t("Certificates")}</FormLabel>
                <Textarea
                  name="certificates"
                  placeholder={t("Write somethings ...")}
                  value={formik.values.certificates}
                  onChange={formik.handleChange}
                  resize="none"
                  maxLength={MAX_LENGTH_TEXTAREA}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t("Awards and other history  ")}</FormLabel>
                <Textarea
                  name="awards"
                  placeholder={t("Write somethings ...")}
                  value={formik.values.awards}
                  onChange={formik.handleChange}
                  resize="none"
                  maxLength={MAX_LENGTH_TEXTAREA}
                />
              </FormControl>
            </Stack>
            <Divider />
            <Stack gap={4}>
              <FormControl
                isInvalid={
                  formik.touched.workExperiences &&
                  formik.errors.workExperiences
                }
              >
                <Text as="strong">{t("Work experience")}</Text>
                <Stack gap={6}>
                  {formik.values?.workExperiences?.map((item, index) => (
                    <WorkExperienceBox
                      key={index}
                      index={index}
                      name={`workExperiences[${index}]`}
                      onChange={(value) =>
                        handleWorkExperienceBoxContentChange(index, value)
                      }
                      values={item}
                      onRemoveClick={onRemoveWorkExperienceClick}
                    />
                  ))}
                  <FormErrorMessage>
                    {formik.errors.workExperiences}
                  </FormErrorMessage>
                </Stack>
              </FormControl>
              <Button
                w="fit-content"
                color="secondary"
                variant="ghost"
                leftIcon={<FaCirclePlus />}
                onClick={onAddWorkExperienceClick}
              >
                {t("Add more")}
              </Button>
            </Stack>
            <Divider />
            <Stack gap={4}>
              <Text as="strong">{t("Introduce yourself")}</Text>
              <FormControl>
                <FormLabel>{t("Self introduction")}</FormLabel>
                <Textarea
                  name="selfIntroduction"
                  placeholder={t("Write somethings ...")}
                  value={formik.values.selfIntroduction}
                  onChange={formik.handleChange}
                  resize="none"
                  maxLength={MAX_LENGTH_TEXTAREA}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t("Introduction video")}</FormLabel>
                <SimpleGrid columns={2} gap={4} mb={4}>
                  {formik.values.userIntroductionVideos.map((item) => {
                    if (item.url) {
                      return (
                        <YoutubeVideoPlayerIframe
                          key={item.url}
                          url={item.url}
                          aspectRatio={16 / 9}
                        />
                      );
                    }
                    return null;
                  })}
                </SimpleGrid>
                <Button
                  color="secondary"
                  w="full"
                  variant="outline"
                  onClick={onSelectIntroductionVideoClick}
                >
                  {t("Select introduction video")}
                </Button>
              </FormControl>
              <FormControl
                isInvalid={
                  formik.touched.attachments && formik.errors.attachments
                }
              >
                <Stack>
                  <FormLabel>
                    <Text as="strong">{t("Attachment files")}</Text>
                  </FormLabel>
                  <Text fontSize="sm">
                    {t(
                      "Capacity limit: Up to 3MB, Up to 3 files can be uploaded"
                    )}
                  </Text>
                </Stack>
                <MultipleFileUploadInput
                  name="attachments"
                  accept={ImageAcceptType + ",application/pdf"}
                  values={formik.values.attachments}
                  onUpload={formik.setFieldValue}
                  onRemoveFileClick={onRemoveFileClick}
                />
                <FormErrorMessage>{formik.errors.attachments}</FormErrorMessage>
              </FormControl>
            </Stack>
            <Divider />
            {/* <Stack gap={4}>
              <FormLabel htmlFor="getNotification">
                {t("Recruitment Notification")}
              </FormLabel>
              <FormControl display="flex" alignItems="center">
                <HStack>
                  <Switch
                    id="getNotification"
                    name="getNotification"
                    isChecked={formik.values.getNotification}
                    onChange={(e) =>
                      formik.setFieldValue(e.target.name, e.target.checked)
                    }
                  />
                  <FormLabel
                    htmlFor="getNotification"
                    mb={0}
                    fontWeight="normal"
                  >
                    {formik.values.getNotification ? t("Agree") : t("Disagree")}
                  </FormLabel>
                </HStack>
              </FormControl>
            </Stack> */}
          </Stack>
        </Container>
      </Flex>
    </Stack>
  );
}
